import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Portal,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TransitionGroup } from "react-transition-group";
import { useNotifications } from "../../hooks/useNotifications";
import { IToastNotification } from "../../types/notifications";

/**
 * ToastSystem - Система всплывающих уведомлений
 * Фаза 2: Система уведомлений (Неделя 8: UI компоненты уведомлений)
 */

interface ToastItemProps {
  toast: IToastNotification;
  onClose: (id: string) => void;
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose, position }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("common");

  // Автоматическое закрытие
  useEffect(() => {
    if (!toast.persistent && toast.duration != null && toast.duration > 0) {
      const timer = setTimeout(() => {
        onClose(toast.id);
      }, toast.duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.id, toast.duration, toast.persistent, onClose]);

  // Получение иконки для типа
  const getIcon = useCallback(() => {
    const iconMap = {
      success: <SuccessIcon />,
      error: <ErrorIcon />,
      warning: <WarningIcon />,
      info: <InfoIcon />,
    };
    return iconMap[toast.type];
  }, [toast.type]);

  // Получение severity для Alert
  const getSeverity = useCallback(() => {
    const severityMap = {
      success: "success" as const,
      error: "error" as const,
      warning: "warning" as const,
      info: "info" as const,
    };
    return severityMap[toast.type];
  }, [toast.type]);

  // Направление анимации в зависимости от позиции
  const getSlideDirection = useCallback(() => {
    if (position.includes("left")) return "right";
    if (position.includes("right")) return "left";
    return "up";
  }, [position]);

  return (
    <Slide
      direction={getSlideDirection()}
      in={true}
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <Box
        sx={{
          mb: 1,
          maxWidth: isMobile ? "100%" : 400,
          minWidth: isMobile ? "100%" : 300,
        }}
      >
        <Alert
          severity={getSeverity()}
          icon={getIcon()}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Кастомные действия */}
              {toast.actions?.map((action, index) => (
                <Button
                  key={index}
                  size="small"
                  variant={action.variant || "text"}
                  onClick={action.action}
                  sx={{ minWidth: "auto" }}
                >
                  {action.label}
                </Button>
              ))}

              {/* Кнопка закрытия */}
              <IconButton
                size="small"
                aria-label={t("notifications.toasts.close")}
                onClick={() => onClose(toast.id)}
                sx={{ ml: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{
            boxShadow: theme.shadows[4],
            borderRadius: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <AlertTitle sx={{ mb: 0.5 }}>{toast.title}</AlertTitle>
          {toast.message}
        </Alert>
      </Box>
    </Slide>
  );
};

interface ToastContainerProps {
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  toasts: IToastNotification[];
  onClose: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  position,
  toasts,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Позиционирование контейнера
  const getContainerStyles = useMemo(() => {
    const baseStyles = {
      position: "fixed" as const,
      zIndex: theme.zIndex.snackbar,
      pointerEvents: "none" as const,
      display: "flex",
      flexDirection: "column" as const,
      gap: 1,
      p: 2,
      maxHeight: "80vh",
      overflow: "hidden",
    };

    if (isMobile) {
      return {
        ...baseStyles,
        top: "auto",
        bottom: 16,
        left: 16,
        right: 16,
        flexDirection: "column-reverse" as const,
      };
    }

    const positionStyles = {
      "top-left": { top: 16, left: 16 },
      "top-right": { top: 16, right: 16 },
      "bottom-left": {
        bottom: 16,
        left: 16,
        flexDirection: "column-reverse" as const,
      },
      "bottom-right": {
        bottom: 16,
        right: 16,
        flexDirection: "column-reverse" as const,
      },
    };

    return {
      ...baseStyles,
      ...positionStyles[position],
    };
  }, [position, theme.zIndex.snackbar, isMobile]);

  if (toasts.length === 0) return null;

  return (
    <Box sx={getContainerStyles}>
      <Box sx={{ pointerEvents: "auto" }}>
        <TransitionGroup>
          {toasts.map((toast) => (
            <Collapse key={toast.id} timeout={200}>
              <ToastItem toast={toast} onClose={onClose} position={position} />
            </Collapse>
          ))}
        </TransitionGroup>
      </Box>
    </Box>
  );
};

const ToastSystem: React.FC = () => {
  const { toasts, hideToast } = useNotifications();

  // Группировка toast по позициям
  const toastsByPosition = useMemo(() => {
    const grouped: Record<string, IToastNotification[]> = {
      "top-left": [],
      "top-right": [],
      "bottom-left": [],
      "bottom-right": [],
    };

    toasts.forEach((toast) => {
      const position = toast.position || "top-right";
      const arr = grouped[position];
      if (arr != null) arr.push(toast);
    });

    return grouped;
  }, [toasts]);

  return (
    <Portal>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <ToastContainer
          key={position}
          position={position as any}
          toasts={positionToasts}
          onClose={hideToast}
        />
      ))}
    </Portal>
  );
};

// Хук для создания toast уведомлений с предустановленными действиями
export const useToast = () => {
  const { showToast } = useNotifications();
  const { t } = useTranslation("common");

  const showSuccess = useCallback(
    (title: string, message: string, options?: Partial<IToastNotification>) => {
      showToast({
        type: "success",
        title,
        message,
        duration: 4000,
        ...options,
      });
    },
    [showToast]
  );

  const showError = useCallback(
    (title: string, message: string, options?: Partial<IToastNotification>) => {
      showToast({
        type: "error",
        title,
        message,
        duration: 6000,
        persistent: true,
        ...options,
      });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message: string, options?: Partial<IToastNotification>) => {
      showToast({
        type: "warning",
        title,
        message,
        duration: 5000,
        ...options,
      });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message: string, options?: Partial<IToastNotification>) => {
      showToast({
        type: "info",
        title,
        message,
        duration: 4000,
        ...options,
      });
    },
    [showToast]
  );

  const showUndoToast = useCallback(
    (
      title: string,
      message: string,
      onUndo: () => void,
      options?: Partial<IToastNotification>
    ) => {
      showToast({
        type: "info",
        title,
        message,
        duration: 8000,
        actions: [
          {
            label: t("notifications.toasts.undo"),
            action: onUndo,
            variant: "outlined",
          },
        ],
        ...options,
      });
    },
    [showToast, t]
  );

  const showRetryToast = useCallback(
    (
      title: string,
      message: string,
      onRetry: () => void,
      options?: Partial<IToastNotification>
    ) => {
      showToast({
        type: "error",
        title,
        message,
        persistent: true,
        actions: [
          {
            label: t("notifications.toasts.retry"),
            action: onRetry,
            variant: "contained",
          },
        ],
        ...options,
      });
    },
    [showToast, t]
  );

  const showLoadingToast = useCallback(
    (title: string, message: string, options?: Partial<IToastNotification>) => {
      return showToast({
        type: "info",
        title,
        message,
        persistent: true,
        ...options,
      });
    },
    [showToast]
  );

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUndoToast,
    showRetryToast,
    showLoadingToast,
    showToast,
  };
};

export default ToastSystem;
